'use client';

/* eslint-disable import/no-extraneous-dependencies */
import { isClient } from '@fe-common-utils/libs/utils';
import { styled } from '@linaria/react';
import { default as classnames, default as cx } from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

interface ModalProperty {
  wrapperId?: string;
  modalClassName?: string;
  bodyClassName?: string;
  overlayClassName?: string;
  children: React.ReactNode;
  fade?: boolean;
  defaultOpened?: boolean;
  hasBackdrop?: boolean;
  disableClose?: boolean;
  defaultHide?: boolean;
  disableButton?: React.ReactNode;
  disableScroll?: boolean;
  zIndex?: number;
  onDidClose?: () => void;
}

export type ModalHandle = {
  open: () => void;
  close: () => void;
  show: () => void;
  hide: () => void;
  isOpen: () => boolean;
  isHide: () => boolean;
};

const ModalWrapper = styled.div<{ zIndex: number }>`
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${({ zIndex }) => zIndex};
  visibility: visible;

  &.is-close {
    width: 0;
    height: 0;
  }

  &.is-hide {
    visibility: hidden;
  }
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0px;
  left: 0;
  background-color: #242424;
  opacity: 0.7;
  z-index: 10;
`;

const ModalBody = styled.div`
  position: relative;
  display: flex;
  z-index: 10;
`;

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 30px;
  color: #515c69;
  border-radius: 50px;
  border: 1px solid #e2e8f1;
  width: 50px;
  height: 50px;
  vertical-align: middle;
`;

/**
 * 當 Modal 顯示時，阻止頁面滾動
 */
function blockPageScroll(enable = true): void {
  const body = document.querySelector('body');
  if (!body) return;

  const overflowStatus = enable ? 'hidden' : 'auto';
  body.style.overflowY = overflowStatus;
}

const Modal = forwardRef<ModalHandle, ModalProperty>(
  (
    {
      wrapperId = 'react-modal',
      modalClassName = '',
      bodyClassName = '',
      overlayClassName = '',
      children,
      fade = true,
      defaultOpened = false,
      defaultHide = false,
      hasBackdrop = true,
      disableClose = true,
      disableButton = null,
      disableScroll = true,
      zIndex = 10,
      onDidClose
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpened);
    const [isHide, setIsHide] = useState(defaultHide);

    const open = useCallback(() => setIsOpen(true), []);

    const close = useCallback(() => {
      setIsOpen(false);
      onDidClose && onDidClose();
    }, [onDidClose]);

    const show = useCallback(() => setIsHide(false), []);

    const hide = useCallback(() => setIsHide(true), []);

    useEffect(() => {
      const isBlock = disableScroll && !isHide && isOpen;
      blockPageScroll(isBlock);
    }, [disableScroll, isHide, isOpen]);

    useImperativeHandle(ref, () => ({ open, close, show, hide, isOpen: () => isOpen, isHide: () => isHide }));

    const onPressEscapeToClose = useCallback(
      (event: KeyboardEvent) => {
        const escKey = 'Escape';
        if (event.key === escKey) close();
      },
      [close]
    );

    const onClickOverlayToClose = useCallback(() => {
      if (hasBackdrop) close();
    }, [close, hasBackdrop]);

    /**
     * 執行監聽鍵盤 esc 鍵關閉 Modal 操作
     */
    useEffect(() => {
      if (isClient) {
        if (isOpen) document.addEventListener('keydown', onPressEscapeToClose, false);
        if (!isOpen) document.removeEventListener('keydown', onPressEscapeToClose, false);
      }
    }, [onPressEscapeToClose, isOpen]);

    return (
      <ModalWrapper
        id={wrapperId}
        zIndex={zIndex}
        className={cx(modalClassName, { 'is-close': !isOpen, 'is-hide': isHide })}
      >
        {isOpen && (
          <>
            <Overlay className={overlayClassName} onClick={onClickOverlayToClose} />
            <ModalBody className={bodyClassName}>
              {disableClose && (
                <CloseButton
                  role='button'
                  className={classnames('modalClose', { fade })}
                  aria-label='close'
                  onClick={close}
                >
                  x
                </CloseButton>
              )}
              {children}
              {disableButton}
            </ModalBody>
          </>
        )}
      </ModalWrapper>
    );
  }
);

export default Modal;
